import type { AnswerAction, AnswerSource, Attachment, ChatMessage, DemoRole, Mode } from "../types.ts";
import type { Strings } from "../core/i18n.ts";
import { renderMarkdown } from "../core/markdown.ts";
import type { FeedbackCategory } from "../core/feedback.ts";
import { createFeedbackForm, settleFeedbackForm } from "./feedback-form.ts";
import { listenFrameHeights } from "./html-preview.ts";
import { renderProposal } from "./proposal.ts";
import { el, Listeners } from "./dom.ts";
import { icon } from "./icons.ts";
import { amiFace, Blinker, petBase } from "./mascot.ts";
import { AmiPet } from "./pet.ts";
import { FilePreview, fileIcon, fileKind } from "./preview.ts";

export interface ViewCallbacks {
  onSubmit(text: string): void;
  onStop(): void;
  onToggle(): void;
  onClose(): void;
  onReset(): void;
  onSize(size: "compact" | "wide" | "full"): void;
  onMicToggle(): void;
  onPromptPick(text: string): void;
  onAction(action: AnswerAction, messageId: string | null): void;
  onRetry(messageId: string): void;
  /** Bấm Xác nhận / Huỷ trên thẻ đề xuất hành động. */
  onProposal(messageId: string, proposalId: string, decision: "confirm" | "cancel", note?: string, edits?: Record<string, string>): void;
  /** Người dùng sửa người nhận / CC / tiêu đề trên thẻ thư (rời ô) — kiểm tra lại ngay. */
  onProposalEdit(messageId: string, proposalId: string, edits: Record<string, string>): void;
  /** Thanh công cụ dưới câu trả lời: sao chép, lưu báo cáo, 👍, 👎. */
  /** 👎 đi kèm lý do người dùng chọn ở màn chọn lý do (Story 5.6); các nút khác không có. */
  onTool(messageId: string, tool: "copy" | "save" | "up" | "down", feedback?: { category: FeedbackCategory; note?: string }): void;
  /** Người dùng chọn / dán / kéo thả tệp vào ô nhập. */
  onAttach(files: File[]): void;
  onRemoveAttachment(key: string): void;
  /** Bản demo: chọn vai khác ở menu góc trên bên phải. */
  onRole(token: string): void;
  /** Bấm một tệp đính kèm trong tin nhắn ⇒ xem trước trong khung chat. */
  onPreviewFile(file: Attachment): void;
}

/** Tệp đang chờ gửi kèm câu hỏi (đang tải lên / đã sẵn sàng / lỗi). */
export interface PendingAttachment {
  key: string;
  name: string;
  status: "uploading" | "ready" | "error";
  error?: string;
}

export interface ViewRefs {
  root: HTMLDivElement;
  scrim: HTMLButtonElement;
  panel: HTMLElement;
  avatar: HTMLSpanElement;
  title: HTMLHeadingElement;
  subtitle: HTMLParagraphElement;
  reset: HTMLButtonElement;
  close: HTMLButtonElement;
  widen: HTMLButtonElement;
  full: HTMLButtonElement;
  scroll: HTMLDivElement;
  intro: HTMLDivElement;
  thread: HTMLDivElement;
  composer: HTMLDivElement;
  form: HTMLFormElement;
  input: HTMLTextAreaElement;
  mic: HTMLButtonElement;
  send: HTMLButtonElement;
  stop: HTMLButtonElement;
  meter: HTMLParagraphElement;
  counter: HTMLSpanElement;
  launcher: HTMLButtonElement;
  live: HTMLDivElement;
  /* chế độ Hub (toàn màn hình): thanh trên, cột trái, cột phải, vùng tab khác */
  hubbar: HTMLDivElement;
  rolebtn: HTMLButtonElement;
  rolemenu: HTMLDivElement;
  attach: HTMLButtonElement;
  fileInput: HTMLInputElement;
  chips: HTMLDivElement;
  frame: HTMLDivElement;
  side: HTMLElement;
  main: HTMLDivElement;
  aside: HTMLElement;
  hubview: HTMLDivElement;
}

/**
 * Tất cả thao tác DOM nằm ở đây. Controller không chạm vào element nào trực
 * tiếp — đổi giao diện không phải đụng vào logic chat.
 *
 * Widget KHÔNG tự thu hẹp nội dung của app chủ. Nó chỉ chiếm chỗ của chính nó
 * và báo bề rộng qua sự kiện `open`; app chủ biết grid của nó, widget không.
 */
/** Ô đã sửa trên thẻ thư (so với giá trị lúc vẽ); null nếu không có gì đổi. */
function propEdits(card: Element | null | undefined): Record<string, string> | null {
  if (!card) return null;
  const out: Record<string, string> = {};
  for (const input of Array.from(card.querySelectorAll<HTMLInputElement>(".apx-prop-edit"))) {
    const key = input.dataset.edit;
    if (key && input.value.trim() !== (input.dataset.orig || "").trim()) out[key] = input.value.trim();
  }
  return Object.keys(out).length ? out : null;
}

export class View {
  readonly host: HTMLElement;
  readonly shadow: ShadowRoot;
  private unlistenFrames: () => void;
  readonly refs: ViewRefs;

  private listeners = new Listeners();
  private callbacks: ViewCallbacks;
  private strings: Strings;
  private markdown: boolean;
  private maxLength: number;
  private locked = false;
  /** Chỉ tự cuộn khi người dùng đang ở đáy — kéo lên đọc lại mà bị giật xuống
   *  là lỗi khó chịu nhất của mọi khung chat. */
  private stick = true;
  private nodes = new Map<string, HTMLElement>();
  private serverTools = false;
  private toolMarks = new Map<string, string>();
  private pendingNode: HTMLDivElement | null = null;
  /** Panel có đang che kín màn hình không. Chỉ khi đó mới nhốt Tab. */
  private modal = false;

  /**
   * Nhốt Tab trong panel — CHỈ khi panel là modal.
   *
   * Ở chế độ dock trên desktop, panel nằm cạnh nội dung và app vẫn dùng được,
   * nên nhốt Tab là sai: người dùng bàn phím muốn quay lại app sẽ bị kẹt trong
   * khung chat. Trên mobile panel che kín màn hình nên nhốt mới đúng.
   */
  private onPanelKeydown = (event: Event): void => {
    const e = event as KeyboardEvent;
    if (!this.modal || e.key !== "Tab") return;

    const items = this.focusables();
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    /* Focus nằm trong shadow root thì document.activeElement trả về host —
       phải hỏi chính shadow root mới biết phần tử thật. */
    const current = this.shadow.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      if (!current || current === first || !items.includes(current)) {
        e.preventDefault();
        last.focus();
      }
    } else if (current === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /** Phần tử nhận được Tab, theo đúng thứ tự DOM. */
  private focusables(): HTMLElement[] {
    const nodes = this.refs.panel.querySelectorAll<HTMLElement>(
      "button, textarea, input, select, a[href], [tabindex]",
    );
    return Array.from(nodes).filter((node) => {
      if (node.hasAttribute("hidden")) return false;
      if ((node as HTMLButtonElement).disabled) return false;
      if (node.getAttribute("tabindex") === "-1") return false;
      /* Ẩn bằng CSS thì không nhận được focus — ví dụ cả form khi chạm trần
         hạn mức. */
      return !!(node.offsetWidth || node.offsetHeight || node.getClientRects().length);
    });
  }

  /** `styles` = the widget stylesheet text, injected by the entry (`widget.ts` imports the .css through the bundler's text loader). View does
   *  not import CSS itself so that it loads under plain `node --test` (test/view-feedback.test.mjs drives the real View on a DOM). */
  constructor(opts: { strings: Strings; markdown: boolean; maxLength: number; callbacks: ViewCallbacks; styles: string }) {
    this.strings = opts.strings;
    this.markdown = opts.markdown;
    this.maxLength = opts.maxLength;
    this.callbacks = opts.callbacks;

    this.host = document.createElement("apus-assistant");
    this.shadow = this.host.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = opts.styles;
    this.shadow.appendChild(style);

    this.refs = this.build();
    this.shadow.appendChild(this.refs.root);
    this.bind();
    this.unlistenFrames = listenFrameHeights(this.shadow);
    this.blinker = new Blinker(this.shadow);
    this.preview = new FilePreview(this.refs.panel);
  }

  /** Khung xem trước tệp trong panel. */
  readonly preview: FilePreview;

  private build(): ViewRefs {
    const s = this.strings;

    const root = el("div", "apx-root");
    root.dataset.open = "false";
    root.dataset.mode = "dock";
    root.dataset.anchor = "right";

    /* Nền mờ chỉ tồn tại ở mobile, nơi panel chiếm toàn màn. */
    const scrim = el("button", "apx-scrim", { type: "button", tabindex: "-1", "aria-hidden": "true" });

    /* `tabindex="-1"` để mở panel trên mobile thì focus được vào chính nó mà
       không rơi vào ô nhập — focus ô nhập là bật bàn phím che mất hội thoại. */
    const panel = el("section", "apx-panel", {
      role: "complementary",
      tabindex: "-1",
      "aria-label": s.title,
    });

    /* -------- head -------- */
    const head = el("div", "apx-head");
    const avatar = el("span", "apx-avatar");
    avatar.innerHTML = icon("spark", 18);

    const headtext = el("div", "apx-headtext");
    const title = el("h2", "apx-title");
    title.textContent = s.title;
    const subtitle = el("p", "apx-sub");
    subtitle.textContent = s.subtitle;
    headtext.append(title, subtitle);

    const reset = el("button", "apx-headbtn apx-reset", { type: "button", title: s.newChat, "aria-label": s.newChat });
    reset.innerHTML = icon("refresh", 15);

    const close = el("button", "apx-headbtn apx-close", { type: "button", title: s.close, "aria-label": s.close });
    close.innerHTML = icon("close", 16);

    /* Mở rộng / toàn màn hình: biểu đồ và bảng cần chỗ (ẩn trên mobile — panel vốn đã toàn màn) */
    const widen = el("button", "apx-headbtn apx-widen", { type: "button", title: "Mở rộng", "aria-label": "Mở rộng", "aria-pressed": "false" });
    widen.innerHTML = icon("widen", 15);
    const full = el("button", "apx-headbtn apx-fullbtn", { type: "button", title: "Toàn màn hình", "aria-label": "Toàn màn hình", "aria-pressed": "false" });
    full.innerHTML = icon("full", 15);

    /* góc trên bên phải: tên + chức vụ; bản demo bấm vào để đổi vai */
    const rolebtn = el("button", "apx-rolebtn", { type: "button", hidden: "", "aria-haspopup": "menu", "aria-expanded": "false", title: "Đổi vai (bản demo)" });
    head.append(avatar, headtext, rolebtn, widen, full, reset, close);
    const rolemenu = el("div", "apx-rolemenu", { role: "menu", hidden: "", "aria-label": "Đổi vai (bản demo)" });

    /* -------- scroll -------- */
    const scroll = el("div", "apx-scroll");
    const intro = el("div", "apx-intro");
    const thread = el("div", "apx-thread", { role: "log", "aria-live": "polite", "aria-relevant": "additions" });
    scroll.append(intro, thread);

    /* -------- composer -------- */
    const composer = el("div", "apx-composer");
    const form = el("form", "apx-form");
    const input = el("textarea", "apx-input", {
      rows: "1",
      placeholder: s.placeholder,
      "aria-label": s.placeholder,
      autocomplete: "off",
      autocapitalize: "sentences",
      spellcheck: "false",
      maxlength: String(this.maxLength),
    });
    const mic = el("button", "apx-act apx-mic", {
      type: "button",
      hidden: "",
      "aria-pressed": "false",
      title: s.record,
      "aria-label": s.record,
    });
    mic.innerHTML = icon("mic", 16);
    const send = el("button", "apx-act apx-send", { type: "submit", title: s.send, "aria-label": s.send });
    send.innerHTML = icon("send", 16);
    send.disabled = true;
    const stop = el("button", "apx-act apx-stop", {
      type: "button",
      hidden: "",
      title: s.stop,
      "aria-label": s.stop,
    });
    stop.innerHTML = icon("stop", 16);
    /* đính kèm ảnh/PDF — chỉ hiện khi có server (Hub) */
    const attach = el("button", "apx-act apx-attach", { type: "button", hidden: "", title: "Đính kèm ảnh, PDF, Word, Excel, PowerPoint hoặc video", "aria-label": "Đính kèm tệp" });
    attach.innerHTML = icon("clip", 16);
    const fileInput = el("input", "", { type: "file", hidden: "", multiple: "", accept: "image/png,image/jpeg,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,video/mp4,video/webm,.docx,.xlsx,.pptx", tabindex: "-1" });
    form.append(attach, input, mic, send, stop, fileInput);
    const chips = el("div", "apx-chips", { hidden: "" });

    /* Dòng dưới ô nhập: hạn mức bên trái, đếm ký tự bên phải. Mục 6.2 yêu cầu
       "luôn cho biết số câu còn lại trong ngày". */
    const meterRow = el("div", "apx-meterrow");
    const meter = el("p", "apx-meter");
    const counter = el("span", "apx-counter");
    meterRow.append(meter, counter);

    composer.append(chips, form, meterRow);

    const live = el("div", "apx-sr", { role: "status", "aria-live": "polite" });

    /* Hub: chỉ hiện ở cỡ "full" khi có server — các vùng rỗng, src/hub/hub.ts lấp đầy */
    const hubbar = el("div", "apx-hubbar", { hidden: "" });
    const side = el("nav", "apx-side", { hidden: "", "aria-label": "Lịch sử hội thoại" });
    const main = el("div", "apx-main");
    main.append(scroll, composer);
    const aside = el("aside", "apx-aside", { hidden: "", "aria-label": "Chi tiết câu trả lời" });
    const hubview = el("div", "apx-hubview", { hidden: "" });
    const frame = el("div", "apx-frame");
    frame.append(side, main, aside, hubview);

    panel.append(head, hubbar, frame, live, rolemenu);

    /* -------- launcher (tuỳ chọn; app chủ thường có nút Ask riêng) -------- */
    const launcher = el("button", "apx-launcher", { type: "button", "aria-expanded": "false", "aria-label": s.open, hidden: "" });
    launcher.innerHTML = icon("chat", 24) + icon("close", 24);

    root.append(scrim, panel, launcher);

    return {
      root, scrim, panel, avatar, title, subtitle, reset, close, widen, full,
      scroll, intro, thread, composer, form, input, mic, send, stop,
      meter, counter, launcher, live,
      hubbar, rolebtn, rolemenu, attach, fileInput, chips, frame, side, main, aside, hubview,
    };
  }

  private bind(): void {
    const r = this.refs;
    const L = this.listeners;

    L.add(r.launcher, "click", () => this.callbacks.onToggle());
    L.add(r.close, "click", () => this.callbacks.onClose());
    L.add(r.scrim, "click", () => this.callbacks.onClose());
    L.add(r.reset, "click", () => this.callbacks.onReset());
    L.add(r.widen, "click", () => this.callbacks.onSize(r.root.dataset.size === "compact" || !r.root.dataset.size ? "wide" : "compact"));
    L.add(r.full, "click", () => this.callbacks.onSize(r.root.dataset.size === "full" ? "compact" : "full"));
    L.add(r.mic, "click", () => this.callbacks.onMicToggle());
    L.add(r.attach, "click", () => r.fileInput.click());
    L.add(r.rolebtn, "click", () => this.toggleRoleMenu(r.rolebtn));
    L.add(r.rolemenu, "click", (event) => {
      const tok = ((event.target as HTMLElement).closest("[data-role-token]") as HTMLElement | null)?.dataset.roleToken;
      if (!tok) return;
      this.toggleRoleMenu(null, false);
      if (tok !== this.demoCurrent) this.callbacks.onRole(tok);
    });
    L.add(r.panel, "click", (event) => {
      const t = event.target as HTMLElement;
      if (!r.rolemenu.hidden && !t.closest(".apx-rolemenu") && !t.closest(".apx-rolebtn") && !t.closest("[data-bar='who']")) this.toggleRoleMenu(null, false);
    });
    L.add(r.fileInput, "change", () => {
      const files = Array.from(r.fileInput.files || []);
      r.fileInput.value = "";
      if (files.length) this.callbacks.onAttach(files);
    });
    L.add(r.input, "paste", (event) => {
      if (r.attach.hidden) return;
      const files = Array.from((event as ClipboardEvent).clipboardData?.files || []);
      if (files.length) {
        event.preventDefault();
        this.callbacks.onAttach(files);
      }
    });
    L.add(r.composer, "dragover", (event) => {
      if (!r.attach.hidden) event.preventDefault();
    });
    L.add(r.composer, "drop", (event) => {
      if (r.attach.hidden) return;
      event.preventDefault();
      const files = Array.from((event as DragEvent).dataTransfer?.files || []);
      if (files.length) this.callbacks.onAttach(files);
    });
    L.add(r.chips, "click", (event) => {
      const key = ((event.target as HTMLElement).closest("[data-unattach]") as HTMLElement | null)?.dataset.unattach;
      if (key) this.callbacks.onRemoveAttachment(key);
    });
    L.add(r.stop, "click", () => this.callbacks.onStop());

    L.add(r.form, "submit", (event) => {
      event.preventDefault();
      this.submit();
    });

    L.add(r.input, "keydown", (event) => {
      const e = event as KeyboardEvent;
      /* Enter gửi, Shift+Enter xuống dòng. Trong lúc bộ gõ tiếng Việt đang
         dựng chữ (isComposing) thì Enter là để chọn chữ, không phải để gửi. */
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        this.submit();
      }
    });

    L.add(r.input, "input", () => this.syncInput());

    L.add(r.intro, "click", (event) => this.delegate(event));
    L.add(r.thread, "click", (event) => this.delegate(event));
    L.add(r.thread, "change", (event) => {
      const input = event.target as HTMLElement;
      if (!input.classList?.contains("apx-prop-edit")) return;
      const card = input.closest(".apx-prop") as HTMLElement | null;
      const holder = input.closest("[data-id]") as HTMLElement | null;
      const edits = propEdits(card);
      if (card?.dataset.prop && holder?.dataset.id && edits) this.callbacks.onProposalEdit(holder.dataset.id, card.dataset.prop, edits);
    });

    L.add(r.panel, "keydown", this.onPanelKeydown);

    L.add(r.scroll, "scroll", () => {
      const node = r.scroll;
      this.stick = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
    });
  }

  private delegate(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest) return;

    const chip = target.closest(".apx-chip") as HTMLElement | null;
    if (chip) {
      this.callbacks.onPromptPick(chip.textContent?.trim() || "");
      return;
    }

    const action = target.closest(".apx-action") as HTMLElement | null;
    if (action) {
      const index = Number(action.dataset.index);
      const holder = action.closest("[data-id]") as HTMLElement | null;
      const messageId = holder?.dataset.id || null;
      const stored = (action as HTMLElement & { _action?: AnswerAction })._action;
      if (stored) this.callbacks.onAction(stored, messageId);
      else if (Number.isFinite(index)) this.callbacks.onAction({ type: "open_panel", label: action.textContent || "", target: "" }, messageId);
      return;
    }

    const retry = target.closest(".apx-retry") as HTMLElement | null;
    if (retry?.dataset.for) {
      this.callbacks.onRetry(retry.dataset.for);
      return;
    }

    /* "Dùng <địa chỉ quen>": thay đúng địa chỉ nghe sai trong ô Người nhận / CC rồi kiểm tra lại như khi sửa tay */
    const sug = target.closest("[data-suggest-to]") as HTMLElement | null;
    if (sug) {
      const card = sug.closest(".apx-prop") as HTMLElement | null;
      const holder = sug.closest("[data-id]") as HTMLElement | null;
      const from = (sug.dataset.suggestFrom || "").toLowerCase();
      for (const input of Array.from(card?.querySelectorAll<HTMLInputElement>(".apx-prop-edit[data-edit=den], .apx-prop-edit[data-edit=cc]") || [])) {
        const parts = input.value.split(/[,;\s]+/).filter(Boolean);
        if (parts.some((x) => x.toLowerCase() === from)) input.value = parts.map((x) => (x.toLowerCase() === from ? sug.dataset.suggestTo || x : x)).join(", ");
      }
      const edits = propEdits(card);
      (sug as HTMLButtonElement).disabled = true;
      if (card?.dataset.prop && holder?.dataset.id && edits) this.callbacks.onProposalEdit(holder.dataset.id, card.dataset.prop, edits);
      return;
    }

    const prop = target.closest("[data-prop-do]") as HTMLElement | null;
    if (prop) {
      const holder = prop.closest("[data-id]") as HTMLElement | null;
      const decision = prop.dataset.propDo === "confirm" ? "confirm" : "cancel";
      if (holder?.dataset.id && prop.dataset.propId) {
        const note = (prop.closest(".apx-prop")?.querySelector(".apx-prop-override") as HTMLTextAreaElement | null)?.value.trim() || "";
        if (decision === "confirm" && prop.closest(".apx-prop")?.querySelector(".apx-prop-override") && !note) {
          const box = prop.closest(".apx-prop")?.querySelector(".apx-prop-override") as HTMLTextAreaElement;
          box.focus();
          box.classList.add("apx-need");
          return;
        }
        (prop as HTMLButtonElement).disabled = true;
        const edits = decision === "confirm" ? propEdits(prop.closest(".apx-prop")) : null;
        this.callbacks.onProposal(holder.dataset.id, prop.dataset.propId, decision, note || undefined, edits || undefined);
      }
      return;
    }

    const tool = target.closest("[data-tool]") as HTMLElement | null;
    if (tool) {
      const holder = tool.closest("[data-id]") as HTMLElement | null;
      const name = tool.dataset.tool as "copy" | "save" | "up" | "down";
      if (!holder?.dataset.id) return;
      if (name === "down") this.toggleFeedbackForm(holder, tool);
      else this.callbacks.onTool(holder.dataset.id, name);
    }
  }

  /** Gửi nội dung đang có trong ô nhập, y như người dùng bấm Gửi (giọng nói tự gửi dùng đường này). */
  submitInput(): void {
    this.submit();
  }

  private submit(): void {
    if (this.locked) return;
    const text = this.refs.input.value.trim();
    if (!text) return;
    this.refs.input.value = "";
    this.syncInput();
    this.callbacks.onSubmit(text);
  }

  private syncInput(): void {
    const r = this.refs;
    const value = r.input.value;
    const used = value.length;

    /* maxlength của textarea chặn gõ tay, nhưng dán và đọc chính tả thì lọt —
       cắt lại cho chắc. */
    if (used > this.maxLength) {
      r.input.value = value.slice(0, this.maxLength);
    }

    const length = r.input.value.length;
    r.send.disabled = this.locked || !r.input.value.trim();

    /* Chỉ hiện bộ đếm khi đã gần chạm trần — hiện suốt thì chỉ tổ gây nhiễu. */
    const near = length >= this.maxLength * 0.8;
    r.counter.hidden = !near;
    if (near) {
      r.counter.textContent = this.strings.charLimit(length, this.maxLength);
      r.counter.classList.toggle("apx-counter-full", length >= this.maxLength);
    }

    r.input.style.height = "auto";
    r.input.style.height = Math.min(r.input.scrollHeight, 120) + "px";
  }

  /* ------------------------------------------------------------ trạng thái */

  setOpen(open: boolean): void {
    this.refs.root.dataset.open = String(open);
    this.refs.launcher.setAttribute("aria-expanded", String(open));
    this.refs.panel.setAttribute("aria-hidden", String(!open));
    if (open) {
      this.stick = true;
      this.scrollToEnd(true);
    }
  }

  /** Modal thì đổi vai trò ARIA và bật bẫy Tab. */
  setModal(modal: boolean): void {
    this.modal = modal;
    const panel = this.refs.panel;
    panel.setAttribute("role", modal ? "dialog" : "complementary");
    if (modal) panel.setAttribute("aria-modal", "true");
    else panel.removeAttribute("aria-modal");
  }

  isModal(): boolean {
    return this.modal;
  }

  /** Focus lúc mở panel. Mobile thì vào chính panel, desktop thì vào ô nhập. */
  focusOnOpen(): void {
    if (this.modal) {
      this.refs.panel.focus();
      return;
    }
    this.refs.input.focus();
  }

  setMode(mode: Mode): void {
    this.refs.root.dataset.mode = mode;
    this.host.setAttribute("data-mode", mode);
  }

  setAnchor(anchor: string): void {
    this.refs.root.dataset.anchor = anchor === "left" ? "left" : "right";
  }

  setSize(size: "compact" | "wide" | "full"): void {
    const r = this.refs;
    r.root.dataset.size = size;
    const wide = size !== "compact";
    r.widen.setAttribute("aria-pressed", String(wide));
    r.widen.innerHTML = icon(wide ? "narrow" : "widen", 15);
    r.widen.title = wide ? "Thu nhỏ" : "Mở rộng";
    r.widen.setAttribute("aria-label", r.widen.title);
    r.full.setAttribute("aria-pressed", String(size === "full"));
    r.full.title = size === "full" ? "Thoát toàn màn hình" : "Toàn màn hình";
    r.full.setAttribute("aria-label", r.full.title);
    this.scrollToEnd(true);
  }

  setLauncher(visible: boolean): void {
    this.refs.launcher.hidden = !visible;
    this.syncPet();
  }

  /* Chim ĐỨNG + bay vào: chỉ khi có nút nổi (không phải chế độ nhúng inline) và dùng linh vật mặc định. */
  private amiPet: AmiPet | null = null;
  private syncPet(): void {
    const want = !!this.pet && !this.refs.launcher.hidden;
    if (want && !this.amiPet) {
      this.amiPet = new AmiPet({
        root: this.refs.root,
        base: this.pet!,
        title: "Apus Ami",
        status: "Trợ lý AI · sẵn sàng",
        greeting: "Chào bạn, mình là Ami! Cần tra số liệu, soạn báo cáo hay gửi email — bấm vào mình nhé.",
        onOpen: () => this.callbacks.onToggle(),
      });
      this.amiPet.start();
    } else if (!want && this.amiPet) {
      this.amiPet.destroy();
      this.amiPet = null;
    }
  }

  /** Demo: xem lại màn Apus Ami bay vào (bình thường chỉ diễn một lần mỗi phiên). */
  replayMascot(): void {
    this.amiPet?.replay();
  }

  setTheme(theme: "light" | "dark"): void {
    this.host.setAttribute("data-theme", theme);
  }

  setTokens(tokens: Record<string, string>): void {
    for (const name of Object.keys(tokens)) {
      const value = tokens[name];
      if (value) this.host.style.setProperty(name, value);
    }
  }

  setMaxLength(value: number): void {
    this.maxLength = value;
    this.refs.input.setAttribute("maxlength", String(value));
    this.syncInput();
  }

  setStrings(strings: Strings): void {
    this.strings = strings;
    const r = this.refs;
    r.input.setAttribute("placeholder", strings.placeholder);
    r.input.setAttribute("aria-label", strings.placeholder);
    r.send.setAttribute("aria-label", strings.send);
    r.send.setAttribute("title", strings.send);
    r.stop.setAttribute("aria-label", strings.stop);
    r.stop.setAttribute("title", strings.stop);
    r.close.setAttribute("aria-label", strings.close);
    r.close.setAttribute("title", strings.close);
    r.reset.setAttribute("aria-label", strings.newChat);
    r.reset.setAttribute("title", strings.newChat);
    r.launcher.setAttribute("aria-label", strings.open);
    r.mic.setAttribute("aria-label", strings.record);
  }

  setMarkdown(enabled: boolean): void {
    this.markdown = enabled;
  }

  setHeader(opts: { title?: string; subtitle?: string; avatarUrl?: string; placeholder?: string }): void {
    const r = this.refs;
    if (opts.title) {
      r.title.textContent = opts.title;
      r.panel.setAttribute("aria-label", opts.title);
      r.launcher.setAttribute("aria-label", opts.title);
    }
    if (opts.subtitle !== undefined) {
      r.subtitle.textContent = opts.subtitle;
      r.subtitle.hidden = !opts.subtitle;
    }
    if (opts.placeholder) {
      r.input.setAttribute("placeholder", opts.placeholder);
      r.input.setAttribute("aria-label", opts.placeholder);
    }
    /* Chỉ nhận URL http(s) — chặn `javascript:` lọt vào từ cấu hình server. */
    if (opts.avatarUrl && /^https?:\/\//i.test(opts.avatarUrl)) {
      const img = el("img", undefined, { src: opts.avatarUrl, alt: "" });
      r.avatar.replaceChildren(img);
    }
  }

  setVoiceAvailable(available: boolean): void {
    this.refs.mic.hidden = !available;
  }

  /** Ghi âm qua server: đang nghe / đang nhận dạng — báo ngay trong ô nhập (placeholder), khoá micro khi chờ. */
  setVoicePhase(phase: "idle" | "listening" | "transcribing"): void {
    const r = this.refs;
    r.mic.disabled = phase === "transcribing";
    r.mic.setAttribute("aria-busy", String(phase === "transcribing"));
    if (this.locked) return;
    const text = phase === "listening" ? this.strings.listening : phase === "transcribing" ? this.strings.transcribing : this.strings.placeholder;
    r.input.setAttribute("placeholder", text);
  }

  /** Báo lỗi giọng nói NGAY trong ô nhập vài giây (người dùng đang nhìn ô nhập, ⛔ chỉ đọc màn hình mới thấy). */
  voiceNotice(text: string): void {
    this.announce(text);
    if (this.locked) return;
    const input = this.refs.input;
    input.setAttribute("placeholder", text);
    setTimeout(() => {
      if (!this.locked && input.getAttribute("placeholder") === text) input.setAttribute("placeholder", this.strings.placeholder);
    }, 4500);
  }

  setRecording(recording: boolean): void {
    const mic = this.refs.mic;
    mic.classList.toggle("apx-recording", recording);
    mic.setAttribute("aria-pressed", String(recording));
    const label = recording ? this.strings.stopRecord : this.strings.record;
    mic.setAttribute("aria-label", label);
    mic.setAttribute("title", label);
  }

  /** Đang chờ trả lời: hiện nút Dừng, khoá ô nhập. Spec cho phép tối đa một
   *  câu đang xử lý, nên chặn hẳn thay vì huỷ ngầm câu trước. */
  setBusy(busy: boolean): void {
    this.locked = busy;
    const r = this.refs;
    r.stop.hidden = !busy;
    r.send.hidden = busy;
    r.input.disabled = busy;
    r.input.setAttribute("aria-busy", String(busy));
    this.refs.root.toggleAttribute("data-ami-thinking", busy);
    if (busy) r.input.setAttribute("placeholder", this.strings.waiting);
    else r.input.setAttribute("placeholder", this.strings.placeholder);
    this.syncInput();
  }

  /** Chạm trần hạn mức, hoặc trợ lý bị tắt từ xa. Panel vẫn mở để đọc lại. */
  setBlocked(message: string | null): void {
    const r = this.refs;
    if (message) {
      r.form.hidden = true;
      let note = r.composer.querySelector(".apx-blocked") as HTMLParagraphElement | null;
      if (!note) {
        note = el("p", "apx-blocked");
        r.composer.insertBefore(note, r.composer.firstChild);
      }
      note.textContent = message;
    } else {
      r.form.hidden = false;
      r.composer.querySelector(".apx-blocked")?.remove();
    }
  }

  setQuotaLabel(text: string): void {
    this.refs.meter.textContent = text;
    this.refs.meter.hidden = !text;
  }

  setInput(text: string): void {
    if (this.locked) return;
    this.refs.input.value = text.slice(0, this.maxLength);
    this.syncInput();
  }

  focusInput(): void {
    /* Trên mobile, focus vào ô nhập sẽ bật bàn phím che mất hội thoại. */
    if (this.modal) return;
    this.refs.input.focus();
  }

  /** Hộp nhập mật khẩu trong panel (ô type=password — ⛔ window.prompt hiện chữ rõ). Trả mật khẩu, null nếu Huỷ/Esc. */
  askPassword(opts: { title: string; message: string; error?: string; confirm?: string }): Promise<string | null> {
    return new Promise((resolve) => {
      const scrim = el("div", "apx-ask", { role: "dialog", "aria-modal": "true", "aria-label": opts.title });
      const box = el("form", "apx-ask-box");
      const h = el("div", "apx-ask-title");
      h.textContent = opts.title;
      const p = el("p", "apx-ask-msg");
      p.textContent = opts.message;
      const input = el("input", "apx-ask-input", { type: "password", autocomplete: "off", "aria-label": "Mật khẩu", placeholder: "Mật khẩu" }) as HTMLInputElement;
      const err = el("p", "apx-ask-err", opts.error ? {} : { hidden: "" });
      err.textContent = opts.error || "";
      const row = el("div", "apx-ask-buttons");
      const cancel = el("button", "apx-btn apx-btn-ghost", { type: "button" });
      cancel.textContent = "Huỷ";
      const ok = el("button", "apx-btn apx-btn-danger", { type: "submit" });
      ok.textContent = opts.confirm || "Xác nhận";
      row.append(cancel, ok);
      box.append(h, p, input, err, row);
      scrim.append(box);
      this.refs.panel.append(scrim);
      const done = (v: string | null) => {
        scrim.remove();
        resolve(v);
      };
      cancel.addEventListener("click", () => done(null));
      box.addEventListener("submit", (e) => {
        e.preventDefault();
        if (input.value) done(input.value);
        else input.focus();
      });
      scrim.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Escape") {
          e.stopPropagation();
          done(null);
        }
      });
      setTimeout(() => input.focus(), 0);
    });
  }

  announce(text: string): void {
    this.refs.live.textContent = text;
  }

  /* ------------------------------------------------------------- nội dung */

  /** Tóm tắt mở đầu và ba câu gợi ý. Dựng sẵn, không gọi mô hình, không tính
   *  lượt — A2 đòi hiện dưới 1 giây và không có khung trống. */
  renderIntro(opts: {
    summary?: string;
    prompts?: string[];
    actions?: AnswerAction[];
    hidden: boolean;
  }): void {
    const intro = this.refs.intro;
    intro.replaceChildren();
    if (opts.hidden) return;

    if (opts.summary) {
      const box = el("div", "apx-summary");
      box.innerHTML = this.markdown ? renderMarkdown(opts.summary) : "";
      if (!this.markdown) box.textContent = opts.summary;
      intro.appendChild(box);
    }

    if (opts.actions?.length) {
      intro.appendChild(this.buildActions(opts.actions));
    }

    if (opts.prompts?.length) {
      const group = el("p", "apx-group");
      group.textContent = this.strings.suggested;
      intro.appendChild(group);
      const chips = el("div", "apx-chips");
      for (const prompt of opts.prompts) {
        const chip = el("button", "apx-chip", { type: "button" });
        chip.textContent = prompt;
        chips.appendChild(chip);
      }
      intro.appendChild(chips);
    }
  }

  renderAll(messages: ChatMessage[]): void {
    this.refs.thread.replaceChildren();
    this.nodes.clear();
    for (const message of messages) this.appendMessage(message);
    this.scrollToEnd(true);
  }

  appendMessage(message: ChatMessage): HTMLElement {
    const node = el("div", "apx-msg apx-msg-" + message.role);
    node.dataset.id = message.id;
    this.paint(node, message);
    /* Bong bóng "đang gõ" luôn phải nằm cuối. */
    if (this.pendingNode && this.pendingNode.parentNode === this.refs.thread) {
      this.refs.thread.insertBefore(node, this.pendingNode);
    } else {
      this.refs.thread.appendChild(node);
    }
    this.nodes.set(message.id, node);
    this.scrollToEnd();
    return node;
  }

  updateMessage(message: ChatMessage): void {
    const node = this.nodes.get(message.id);
    if (!node) return;
    this.paint(node, message);
    this.scrollToEnd();
  }

  removeMessage(id: string): void {
    this.nodes.get(id)?.remove();
    this.nodes.delete(id);
  }

  private paint(node: HTMLElement, message: ChatMessage): void {
    node.className =
      "apx-msg apx-msg-" +
      message.role +
      (message.error ? " apx-msg-error" : "") +
      (message.streaming ? " apx-msg-streaming" : "");
    node.replaceChildren();

    /* các bước agent đã làm — gập sẵn, mở ra để kiểm tra (như "agent run" của Qonnex) */
    if (message.role === "assistant" && !message.streaming && message.trace && message.trace.length > 1) {
      const det = el("details", "apx-run");
      const last = message.trace[message.trace.length - 1];
      const sum = el("summary", "");
      sum.textContent = "Apus Ami đã làm " + message.trace.length + " bước" + (last ? " · " + (last.t / 1000).toFixed(1).replace(".", ",") + " giây" : "");
      const ol = el("ol", "apx-steps");
      for (const st of message.trace) {
        const li = el("li", "apx-step" + (/internet/i.test(st.label) ? " apx-k-web" : /đề xuất|ghi nhớ/i.test(st.label) ? " apx-k-write" : ""));
        const tm = el("span", "apx-step-t");
        tm.textContent = "+" + (st.t / 1000).toFixed(1).replace(".", ",") + "s";
        const lb = el("span", "");
        lb.textContent = st.label;
        li.append(tm, lb);
        ol.appendChild(li);
      }
      det.append(sum, ol);
      node.appendChild(det);
    }

    const body = el("div", "apx-body");
    /* Tin của người dùng luôn là chữ trần. Chỉ câu trả lời của trợ lý mới đi
       qua renderMarkdown — vốn đã escape trước khi sinh thẻ. */
    if (message.role === "assistant" && this.markdown && !message.error) {
      body.innerHTML = renderMarkdown(message.text, { live: !message.streaming });
    } else {
      body.textContent = message.text;
    }
    node.appendChild(body);

    if (message.attachments?.length) {
      const list = el("div", "apx-attached");
      for (const f of message.attachments) {
        /* bấm để xem trước ngay trong khung chat (PDF, ảnh, video, Word/Excel/PowerPoint) */
        const chip = el("button", "apx-filechip apx-filechip-open", { type: "button", title: "Xem trước " + f.name });
        chip.innerHTML = icon(fileIcon(f.type), 12);
        chip.append(document.createTextNode(" " + f.name + " · " + fileKind(f.type, f.name) + " · " + Math.max(1, Math.round(f.size / 1024)) + " KB"));
        chip.addEventListener("click", () => this.callbacks.onPreviewFile(f));
        list.appendChild(chip);
      }
      node.appendChild(list);
    }

    if (message.sampleData) {
      const badge = el("span", "apx-sample");
      badge.textContent = this.strings.sampleData;
      node.appendChild(badge);
    }

    /* Khối nguồn nằm ngay dưới câu trả lời, không giấu sau nút bấm. */
    if (message.sources?.length && !message.streaming) {
      node.appendChild(this.buildSources(message.sources));
    }

    if (message.actions?.length && !message.streaming) {
      node.appendChild(this.buildActions(message.actions));
    }

    /* Đề xuất hành động: AI không tự ghi — người dùng xác nhận trên thẻ */
    if (message.proposals?.length && !message.streaming) {
      for (const p of message.proposals) node.appendChild(renderProposal(p, "chat"));
    }

    if (message.role === "assistant" && !message.streaming && !message.error && message.text.trim()) {
      node.appendChild(this.buildTools(message));
    }

    /* gợi ý câu tiếp theo — CSS chỉ cho hiện dưới câu trả lời MỚI NHẤT */
    if (message.role === "assistant" && !message.streaming && message.followUps?.length) {
      const box = el("div", "apx-followups");
      const lb = el("span", "apx-followups-l");
      lb.textContent = "Hỏi tiếp";
      box.appendChild(lb);
      for (const q of message.followUps) {
        const b = el("button", "apx-chip apx-followup", { type: "button" });
        b.textContent = q;
        box.appendChild(b);
      }
      node.appendChild(box);
    }

    if (message.error) {
      const retry = el("button", "apx-retry", { type: "button" });
      retry.dataset.for = message.id;
      retry.innerHTML = icon("refresh", 12, 2.4) + "<span></span>";
      (retry.lastElementChild as HTMLElement).textContent = this.strings.retry;
      node.appendChild(retry);
    }
  }

  private buildSources(sources: AnswerSource[]): HTMLElement {
    const box = el("div", "apx-sources");
    for (const source of sources) {
      const row = el("p", "apx-source");
      const parts = [source.label, source.at, source.scope].filter(Boolean) as string[];
      const line = el("span", "apx-source-line");
      if (source.url && /^https?:\/\//i.test(source.url)) {
        /* link nguồn internet: chữ vẫn qua textContent, href chỉ nhận http/https */
        line.append(this.strings.source + ": ");
        const a = el("a", "apx-a", { href: source.url, target: "_blank", rel: "noopener noreferrer nofollow" });
        a.textContent = parts.join(" · ") || source.url;
        line.appendChild(a);
      } else {
        line.textContent = this.strings.source + ": " + parts.join(" · ");
      }
      row.appendChild(line);
      if (source.note) {
        const note = el("span", "apx-source-note");
        note.textContent = source.note;
        row.appendChild(note);
      }
      box.appendChild(row);
    }
    return box;
  }

  private buildActions(actions: AnswerAction[]): HTMLElement {
    const box = el("div", "apx-actions");
    actions.slice(0, 3).forEach((action, index) => {
      const button = el("button", "apx-action", { type: "button" }) as HTMLButtonElement & { _action?: AnswerAction };
      button.dataset.index = String(index);
      /* Giữ nguyên object thay vì nhét vào data-* : params có thể lồng sâu, và
         serialize qua thuộc tính là mời XSS vào nhà. */
      button._action = action;
      const label = el("span");
      label.textContent = action.label;
      button.append(label, spanIcon(action.type));
      box.appendChild(button);
    });
    return box;
  }

  setPending(pending: boolean, message = this.strings.waiting): void {
    if (pending) {
      if (this.pendingNode) {
        const label = this.pendingNode.querySelector(".apx-typing-label");
        if (label) label.textContent = message;
        return;
      }
      const node = el("div", "apx-typing", {
        role: "status",
        "aria-live": "polite",
        "aria-atomic": "true",
      });
      const dots = el("span", "apx-typing-dots", { "aria-hidden": "true" });
      dots.append(el("span"), el("span"), el("span"));
      const label = el("span", "apx-typing-label");
      label.textContent = message;
      node.append(dots, label);
      this.refs.thread.appendChild(node);
      this.pendingNode = node;
      this.scrollToEnd();
    } else if (this.pendingNode) {
      this.pendingNode.remove();
      this.pendingNode = null;
    }
  }

  clearThread(): void {
    this.setPending(false);
    this.refs.thread.replaceChildren();
    this.nodes.clear();
  }

  scrollToEnd(force = false): void {
    if (!force && !this.stick) return;
    const node = this.refs.scroll;
    /* rAF: đợi trình duyệt tính xong layout của bong bóng vừa thêm. */
    requestAnimationFrame(() => {
      node.scrollTop = node.scrollHeight;
    });
  }

  /** Bật nút cần server (Lưu báo cáo, 👍/👎). Không có server thì chỉ còn Sao chép. */
  setServerTools(on: boolean): void {
    this.serverTools = on;
  }

  /** Nút đã đánh dấu (👍/👎) — nhớ theo tin nhắn trong phiên hiển thị. */
  markTool(messageId: string, tool: string): void {
    this.toolMarks.set(messageId, tool);
    const node = this.nodes.get(messageId);
    node?.querySelectorAll<HTMLElement>("[data-tool]").forEach((b) => b.classList.toggle("apx-on", b.dataset.tool === tool));
  }

  /* ------------------------------------------- linh vật + đổi vai (demo) */
  private mascot = "";
  private pet: string | null = null;
  private blinker: Blinker;
  private demoRoles: DemoRole[] = [];
  private demoCurrent: string | null = null;

  /** Ảnh đầu chim Apus Ami cho bong bóng + ảnh đại diện (chỉ URL http/https). */
  setMascot(url: string | undefined): void {
    if (!url || !/^https?:\/\//i.test(url) || url === this.mascot) return;
    this.mascot = url;
    this.pet = petBase(url);
    const r = this.refs;
    const img = (cls: string): HTMLElement => {
      if (this.pet) return amiFace(this.pet, cls);
      const i = el("img", cls, { src: url, alt: "", draggable: "false" });
      i.addEventListener("error", () => i.remove());
      return i;
    };
    r.launcher.replaceChildren(img("apx-launcher-img"));
    r.launcher.insertAdjacentHTML("beforeend", icon("close", 24));
    r.launcher.classList.add("apx-has-mascot");
    if (!r.avatar.querySelector("img")) r.avatar.replaceChildren(img("apx-avatar-img"));
    this.syncPet();
  }

  mascotUrl(): string {
    return this.mascot;
  }

  /** Khuôn mặt Ami có mắt chớp (cho Hub); null khi host tự đặt ảnh tĩnh khác. */
  mascotFace(cls: string): HTMLElement | null {
    return this.pet ? amiFace(this.pet, cls) : null;
  }

  /** Bản demo: hiện nút "Tên · Chức vụ ▾" và danh sách vai. `roles` rỗng ⇒ ẩn. */
  setDemoRoles(roles: DemoRole[], current: string | null): void {
    this.demoRoles = roles;
    this.demoCurrent = current;
    const r = this.refs;
    const me = roles.find((x) => x.token === current);
    r.rolebtn.hidden = !roles.length || !me;
    if (me) {
      r.rolebtn.replaceChildren();
      const av = el("span", "apx-roleav");
      av.textContent = initials(me.name);
      const tx = el("span", "apx-roletx");
      const n = el("b", "");
      n.textContent = me.name;
      const t = el("small", "");
      t.textContent = me.title;
      tx.append(n, t);
      r.rolebtn.append(av, tx);
      r.rolebtn.insertAdjacentHTML("beforeend", '<span class="apx-caret">▾</span>');
    }
    r.rolemenu.replaceChildren();
    const head = el("div", "apx-rolemenu-h");
    head.textContent = "Bản demo · đăng nhập với vai";
    r.rolemenu.append(head);
    for (const x of roles) {
      const it = el("button", "apx-roleitem" + (x.token === current ? " apx-on" : ""), { type: "button", role: "menuitemradio", "aria-checked": String(x.token === current), "data-role-token": x.token });
      const av = el("span", "apx-roleav");
      av.textContent = initials(x.name);
      const tx = el("span", "apx-roletx");
      const n = el("b", "");
      n.textContent = x.name;
      const t = el("small", "");
      t.textContent = x.title;
      tx.append(n, t);
      it.append(av, tx);
      r.rolemenu.append(it);
    }
  }

  hasDemoRoles(): boolean {
    return this.demoRoles.length > 0;
  }

  /** Mở/đóng menu vai, neo dưới phần tử bấm (nút trong header hoặc tên ở thanh Hub). */
  toggleRoleMenu(anchor: HTMLElement | null, open?: boolean): void {
    const m = this.refs.rolemenu;
    const show = open ?? m.hidden;
    if (!this.demoRoles.length) return;
    m.hidden = !show;
    this.refs.rolebtn.setAttribute("aria-expanded", String(show));
    if (show && anchor) {
      const pr = this.refs.panel.getBoundingClientRect();
      const ar = anchor.getBoundingClientRect();
      m.style.top = Math.round(ar.bottom - pr.top + 6) + "px";
      /* kẹp trong panel: panel hẹp (cỡ thu nhỏ) thì menu ⛔ tràn qua mép trái rồi bị cắt chữ */
      const w = Math.min(270, pr.width - 16);
      m.style.width = w + "px";
      m.style.right = Math.max(8, Math.min(Math.round(pr.right - ar.right), Math.round(pr.width - w - 8))) + "px";
      (m.querySelector(".apx-on") as HTMLElement | null)?.focus();
    }
  }

  /** Bật nút đính kèm (chỉ khi có server). */
  setAttachEnabled(on: boolean): void {
    this.refs.attach.hidden = !on;
  }

  renderPending(list: PendingAttachment[]): void {
    const c = this.refs.chips;
    c.replaceChildren();
    c.hidden = !list.length;
    for (const a of list) {
      const chip = el("span", "apx-filechip apx-fc-" + a.status, { title: a.error || a.name });
      chip.textContent = (a.status === "uploading" ? "Đang tải · " : a.status === "error" ? "Lỗi · " : "") + a.name;
      const x = el("button", "apx-unattach", { type: "button", "data-unattach": a.key, "aria-label": "Bỏ tệp " + a.name });
      x.innerHTML = icon("close", 11);
      chip.appendChild(x);
      c.appendChild(chip);
    }
  }

  private buildTools(message: ChatMessage): HTMLElement {
    const bar = el("div", "apx-tools");
    if (message.intent) {
      const chip = el("span", "apx-intent apx-in-" + message.intent.kind, { title: "Loại tác vụ Apus Ami đã nhận diện" });
      chip.textContent = message.intent.label;
      bar.appendChild(chip);
    }
    const items: Array<[string, string, string]> = [["copy", "copy", "Sao chép"]];
    if (this.serverTools) items.push(["save", "save", "Lưu thành báo cáo"], ["up", "up", "Câu trả lời tốt"], ["down", "down", "Câu trả lời chưa đúng"]);
    const mark = this.toolMarks.get(message.id);
    for (const [name, ico, label] of items) {
      const b = el("button", "apx-tool" + (mark === name ? " apx-on" : ""), { type: "button", "data-tool": name, title: label, "aria-label": label });
      b.innerHTML = icon(ico, 14);
      bar.appendChild(b);
    }
    return bar;
  }

  /** Màn chọn lý do 👎 ngay dưới thanh công cụ của câu trả lời (feedback-form.ts); bấm 👎 lần nữa thì đóng. */
  private toggleFeedbackForm(holder: HTMLElement, anchor: HTMLElement): void {
    const open = holder.querySelector(".apx-fb");
    if (open) {
      open.remove();
      return;
    }
    const messageId = holder.dataset.id;
    if (!messageId) return;
    const form = createFeedbackForm({ onSubmit: (payload) => this.callbacks.onTool(messageId, "down", payload) });
    (anchor.closest(".apx-tools") || anchor).insertAdjacentElement("afterend", form);
    (form.querySelector("[data-fb-cat]") as HTMLElement | null)?.focus();
  }

  /** Gửi xong thì đóng màn chọn lý do; lỗi thì mở lại nút Gửi để thử lại. */
  settleFeedback(messageId: string, ok: boolean): void {
    settleFeedbackForm(this.nodes.get(messageId)?.querySelector(".apx-fb"), ok);
  }

  /** Cuộn tới một tin nhắn và nháy viền (kết quả tìm kiếm trong Hub). */
  focusMessage(id: string): void {
    const node = this.nodes.get(id);
    if (!node) return;
    node.scrollIntoView({ block: "center" });
    node.classList.add("apx-flash");
    setTimeout(() => node.classList.remove("apx-flash"), 1800);
  }

  destroy(): void {
    this.preview.destroy();
    this.amiPet?.destroy();
    this.blinker.stop();
    this.unlistenFrames();
    this.listeners.clear();
    this.nodes.clear();
    this.pendingNode = null;
    this.host.remove();
  }
}

function spanIcon(type: AnswerAction["type"]): HTMLElement {
  const wrap = el("span", "apx-action-ico");
  wrap.innerHTML = icon(type === "link" ? "external" : "arrow", 13, 2.2);
  return wrap;
}

function initials(name: string): string {
  const w = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "?";
  const pick = w.length === 1 ? [w[0]] : [w[w.length - 2], w[w.length - 1]];
  return pick.map((x) => (x ? x.charAt(0) : "")).join("").toUpperCase();
}
